import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../../http';
import CompanyDetailPanel from './CompanyDetailPanel';

const STATUS_COLORS = {
  active: { bg: '#c6f6d5', color: '#22543d', label: 'LIVE' },
  completed: { bg: '#ebf8ff', color: '#2c5282', label: 'Completed' },
  archived: { bg: '#edf2f7', color: '#4a5568', label: 'Archived' },
  setup: { bg: '#fefcbf', color: '#744210', label: 'Setup' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.setup;
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.8rem',
        background: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        minWidth: '130px',
        flex: '1 1 130px',
      }}
    >
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#2d3748' }}>{value}</div>
      <div style={{ fontSize: '0.85rem', color: '#718096' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.2rem' }}>{sub}</div>}
    </div>
  );
}

export default function EventReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`/api/reports/test-events/${id}/company-report`);
      setData(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load report data.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh for active events
  useEffect(() => {
    if (!data?.event || data.event.status !== 'active') return;
    const t = setInterval(fetchData, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.event?.status, fetchData]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(`/api/test-events/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = res.headers['content-disposition'];
      let fileName = `Results-${id}.xlsx`;
      if (contentDisposition) {
        const m = contentDisposition.match(/filename="?([^"]+)"?/);
        if (m) fileName = m[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading report...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#c53030' }}>{error}</div>;
  if (!data) return <div style={{ padding: '2rem' }}>No data.</div>;

  const { event, companies } = data;
  const totalCompanies = companies.length;
  const completedCompanies = companies.filter(
    (c) => c.sessions.length > 0 && c.sessions.every((s) => s.status === 'completed')
  ).length;
  const totalStationsPassed = companies.reduce((sum, c) => sum + c.summary.stations_passed, 0);
  const totalStationsCompleted = companies.reduce((sum, c) => sum + c.summary.stations_completed, 0);
  const overallPassRate =
    totalStationsCompleted > 0
      ? Math.round((totalStationsPassed / totalStationsCompleted) * 1000) / 10
      : null;

  const selectedCompany = selectedCompanyId
    ? companies.find((c) => c.id === selectedCompanyId)
    : null;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{event.name}</h1>
            <StatusBadge status={event.status} />
            {event.status === 'active' && (
              <span style={{ fontSize: '0.8rem', color: '#718096' }}>· auto-refreshes every 30s</span>
            )}
          </div>
          <p style={{ color: '#718096', margin: '0.25rem 0 0' }}>
            {new Date(event.event_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            {event.location ? ` · ${event.location}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {downloading ? '⏳ Generating...' : '📥 Export Excel'}
          </button>
          <Link to="/admin/reports" className="btn btn-secondary">
            ← Back to Reports
          </Link>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <SummaryCard label="Companies" value={totalCompanies} />
        <SummaryCard label="Completed" value={completedCompanies} sub={`of ${totalCompanies}`} />
        <SummaryCard
          label="Station Sessions"
          value={totalStationsCompleted}
          sub={`${totalStationsPassed} passed`}
        />
        {overallPassRate !== null && (
          <SummaryCard
            label="Overall Pass Rate"
            value={`${overallPassRate}%`}
            sub="by station session"
          />
        )}
      </div>

      {/* Company table */}
      {companies.length === 0 ? (
        <p style={{ color: '#a0aec0' }}>No company data recorded yet.</p>
      ) : (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f7fafc', borderBottom: '2px solid #edf2f7' }}>
              <tr>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Department</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Stations Done</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Passed</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Failed</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>In Progress</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => {
                const inProgress = company.sessions.filter(
                  (s) => s.status === 'active'
                ).length;
                const isSelected = selectedCompanyId === company.id;
                return (
                  <React.Fragment key={company.id}>
                    <tr
                      style={{
                        borderBottom: isSelected ? 'none' : '1px solid #edf2f7',
                        background: isSelected ? '#ebf8ff' : 'white',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectedCompanyId(isSelected ? null : company.id)
                      }
                    >
                      <td style={tdStyle}>
                        <strong>{company.name}</strong>
                        {company.program_name && (
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                            {company.program_name}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: '#718096' }}>
                        {company.home_department || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {company.summary.stations_completed}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: '#2f855a', fontWeight: 600 }}>
                        {company.summary.stations_passed}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: company.summary.stations_failed > 0 ? '#c53030' : '#718096', fontWeight: company.summary.stations_failed > 0 ? 600 : 400 }}>
                        {company.summary.stations_failed}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: inProgress > 0 ? '#2b6cb0' : '#718096' }}>
                        {inProgress > 0 ? `${inProgress} active` : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                          style={{
                            background: isSelected ? '#2b6cb0' : '#ebf8ff',
                            color: isSelected ? 'white' : '#2b6cb0',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.3rem 0.75rem',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCompanyId(isSelected ? null : company.id);
                          }}
                        >
                          {isSelected ? 'Close' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {isSelected && selectedCompany && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 1rem' }}>
                          <CompanyDetailPanel
                            company={selectedCompany}
                            onClose={() => setSelectedCompanyId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontWeight: 600,
  color: '#4a5568',
  fontSize: '0.875rem',
};

const tdStyle = {
  padding: '0.75rem 1rem',
  verticalAlign: 'top',
};
